import React from 'react'

import { BaseText } from '@/components/base'
import { currencyUtils } from '@/utils'
import { getDisplayCurrencyCode } from '@/utils/getDisplayCurrencyCode'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'

const { formatFiatCurrency, currencyConditionals } = currencyUtils

interface LabelAmountProps {
  amount: string
  currencyCode?: string
  showDecimals?: boolean
  shouldMaskStablecoinAsFiat?: boolean
  fontSize?: number
  fontFamily?: 'regular' | 'medium' | 'semibold' | 'bold'
  color?: string
  textAlign?: 'left' | 'center' | 'right'
}

export const LabelAmount: React.FC<LabelAmountProps> = ({
  amount,
  currencyCode = 'USD',
  showDecimals = false,
  shouldMaskStablecoinAsFiat = false,
  fontSize = 48,
  fontFamily = 'bold',
  color = 'label.default',
  textAlign = 'center',
}) => {
  const { isStablecoin } = currencyConditionals(currencyCode)
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )

  const formatAmount = () => {
    const numericAmount = parseFloat(amount) || 0

    if (shouldMaskStablecoinAsFiat && isStablecoin) {
      // Show as fiat currency for stablecoins
      return formatFiatCurrency({
        value: numericAmount,
        fiatCode:
          currencyCode == 'USDT' ? 'USD' : displayCurrency?.isin || 'USD',
        showDecimals,
      })
    }

    if (isStablecoin) {
      // Show with currency code for stablecoins
      return `${numericAmount.toFixed(showDecimals ? 2 : 0)} ${getDisplayCurrencyCode(currencyCode)}`
    }

    // Show crypto amount with currency code
    const decimals = showDecimals ? 8 : 4
    return `${numericAmount.toFixed(decimals)} ${getDisplayCurrencyCode(currencyCode)}`
  }

  return (
    <BaseText
      fontSize={fontSize}
      fontFamily={fontFamily}
      color={color}
      textAlign={textAlign}
      style={{
        lineHeight: 1,
      }}
    >
      {formatAmount()}
    </BaseText>
  )
}
